import {
	ArrowDownTrayIcon,
	ArrowPathIcon,
	BanknotesIcon,
	BoltIcon,
	CheckBadgeIcon,
	CheckIcon,
	ClipboardIcon,
	CreditCardIcon,
	DocumentDuplicateIcon,
	DocumentIcon,
	EnvelopeIcon,
	ExclamationTriangleIcon,
	ForwardIcon,
	NewspaperIcon,
	PencilSquareIcon,
} from "@heroicons/react/20/solid";
import BatteryChart from "@/components/common/charts/BatteryChart";
import { format } from "date-fns";
import { useMemo } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Tooltip from "@/tooltip";
import { Actions as COMPANY_ACTIONS } from "@/redux/actions/companyActions";
import { Actions as PAYROLL_ACTIONS } from "@/redux/actions/payrollActions";
import { Actions as PAYSLIP_ACTIONS } from "@/redux/actions/payslipActions";
import { Actions as FSS_ACTIONS } from "@/redux/actions/fssReportActions";
import { getNumberWithOrdinal, roundTo2 } from "@/common/validation/Validators";
import IntegrationPayments from "@/integrations/IntegrationPayments";
import IntegrationSendDataActions from "@/integrations/IntegrationSendDataActions";
import IntegrationSendPayrollData from "@/integrations/IntegrationSendPayrollData";
import { Actions as FYORIN_ACTIONS } from "@/integrations/fyorin/store/fyorinActions";
import ActionList from "@/common/custom-selectors/ActionList";
import InfoCard from "@/components/common/cards/InfoCard";
import { Row } from "@/components/common/custom-lists/ListView";
import { copyToClipboard } from "@/helpers/ClipboardHelpers";

function GoToPayroll({ status, to, onClick, view }) {
	return (
		<Link
			className={`btn btn-xs btn-${status?.className}`}
			style={{ display: "block", width: "fit-content", marginTop: view === "Card" ? undefined : 7 }}
			to={to}
			type="button"
			onClick={onClick}
		>
			<Tooltip
				content={status.title}
				arrowSize={4}
			>
				<status.icon
					className="h-5 w-5 text-dark-gray"
					style={view === "Card" ? { verticalAlign: "middle", marginRight: 2 } : { marginTop: 4 }}
				/>
				{view === "Card" ? status?.action : ""}
			</Tooltip>
		</Link>
	);
}

function CopyId({ view, id }) {
	const copy = () => copyToClipboard(id);

	return (
		<div
			className="btn btn-xs btn-default"
			style={{ display: "block", width: "fit-content", marginTop: view === "Card" ? undefined : 7 }}
			type="button"
			onClick={copy}
		>
			<Tooltip
				content={"Copy ID"}
				arrowSize={4}
			>
				<ClipboardIcon
					className="h-5 w-5 text-dark-gray"
					style={view === "Card" ? { verticalAlign: "middle", marginRight: 2 } : { marginTop: 4 }}
				/>
				{view === "Card" ? "Copy ID" : null}
			</Tooltip>
		</div>
	);
}

const PayrollRow = ({
	period,
	downloadReport,
	sendPayslipsModal,
	concludePayroll,
	paySchedule,
	downloadAllPayslips,
	downloadEmployeeForm,
	payrollUpdateId,
	currency,
	isAdmin,
	userPermissions,
	isUpdating,
	view,
	jurisdiction,
	archivedStatus,
}) => {
	const {
		active,
		periodStart,
		periodEnd,
		paySchedule: periodSchedule,
		payPeriods,
		year,
		id,
		complete,
		summary,
		period: periodNo,
	} = period || {};
	const periodOrdinal = getNumberWithOrdinal(periodNo);
	const periodDuration =
		periodStart && periodEnd
			? `${format(new Date(periodStart), "do MMM")} - ${format(new Date(periodEnd), "do MMM")}`
			: "";
	const title = `${periodOrdinal} ${paySchedule?.name} Payroll ${year}`;
	const notConcluded = !["Open", "Concluded", undefined].includes(period?.status);
	const incomplete = ["false", "no", false, undefined].includes(complete);
	const status = useMemo(() => {
		if (active)
			return {
				className: "primary",
				icon: view === "Card" ? ForwardIcon : PencilSquareIcon,
				title: `Edit ${title}`,
				message: notConcluded ? period?.status : "Active",
				action: "Edit",
			};
		if (incomplete)
			return {
				className: "warning",
				icon: ExclamationTriangleIcon,
				title: "Payroll Not Concluded",
				message: notConcluded ? period?.status : "Payroll Not Concluded",
				action: "Conclude",
			};
		if (!incomplete)
			return {
				className: view === "Card" ? "success" : "default",
				icon: view === "Card" ? CheckIcon : CheckBadgeIcon,
				title: "Complete",
				action: "Concluded",
			};
		return null;
	}, [incomplete, active, period?.status, notConcluded]);

	const to = `/organisation/payroll/${id}`;

	const [total, columns] = useMemo(() => {
		if (!summary?.taxes || incomplete) return [];

		const columns = Object.entries(summary?.taxes || {})?.map(([key, value]) => ({
			name: key,
			value: parseFloat(value?.employer) + parseFloat(value?.employee),
		}));

		const columnsWithNet = [
			{
				name: "Net",
				value: summary?.net,
			},
			...columns,
		];

		const total = columnsWithNet?.reduce((acc, { value }) => acc + value, 0);

		return [total, columnsWithNet];
	}, [JSON.stringify(summary?.taxes), summary?.gross, summary?.net, active, incomplete]);

	const downloads = [
		{
			icon: DocumentDuplicateIcon,
			label: "Payroll Report",
			onClick: () => downloadReport("payroll", period?.id),
		},
		{
			icon: DocumentIcon,
			label: "Payroll Net Report",
			onClick: () => downloadReport("net", period?.id),
		},
		{
			icon: CreditCardIcon,
			label: "Bank + Net Report",
			onClick: () => downloadReport("bank", period?.id),
		},
		{
			icon: BanknotesIcon,
			label: "Cheque Report",
			onClick: () => downloadReport("cheque", period?.id),
		},
		{
			icon: NewspaperIcon,
			label: "Download Payslips",
			onClick: () => downloadAllPayslips(period?.id),
		},
		{
			icon: BanknotesIcon,
			label: "Download Cheques",
			onClick: () =>
				downloadEmployeeForm({
					payPeriodID: period?.id,
					form: "Cheques",
				}),
		},
	];
	const actions = [
		<IntegrationSendDataActions
			periodId={period?.id}
			period={period}
		/>,
		<IntegrationPayments
			period={period}
			action={true}
		/>,
		{
			icon: EnvelopeIcon,
			label: "Send Employee Payslips",
			onClick: () => sendPayslipsModal(period?.id),
		},
		{
			icon: ArrowPathIcon,
			label: "Refresh Payroll Reports",
			className: isUpdating ? "animate-spin" : "",
			onClick: () => {
				if (isUpdating || archivedStatus == "Archived") return;
				concludePayroll(period?.id);
			},
		},
	];

	const actionButtons = (
		<div
			className="btn-group"
			style={{ margin: 0 }}
		>
			<ActionList
				className="btn btn-xs btn-default"
				type="button"
				as="button"
				options={downloads}
				disabled={incomplete}
			>
				<ArrowDownTrayIcon
					className="h-5 w-5 text-dark-gray"
					style={{ verticalAlign: "middle", marginRight: 2 }}
				/>
				Download
			</ActionList>
			<ActionList
				className="btn btn-xs btn-default"
				type="button"
				as="button"
				options={actions}
				disabled={incomplete}
			>
				<BoltIcon
					className="h-5 w-5 text-dark-gray"
					style={{ verticalAlign: "middle", marginRight: 2 }}
				/>
				Actions
			</ActionList>
			{isAdmin && view === "Card" ? (
				<CopyId
					view={view}
					id={id}
				/>
			) : null}
		</div>
	);

	if (view === "Card")
		return (
			<InfoCard
				header={
					<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)" }}>
						<div style={{ display: "flex", gridColumn: "1 / span 2" }}>
							<div
								className={`item item-circle bg-${status?.className}`}
								style={{ position: "relative", height: "4rem", width: "4rem", flexShrink: 0 }}
							>
								<status.icon
									style={{
										position: "absolute",
										height: "3rem",
										color: "white",
										right: "0.5rem",
										top: "0.5rem",
									}}
								/>
							</div>
							<h4 style={{ marginLeft: "1rem" }}>
								{periodOrdinal} {paySchedule?.name ? ` ${paySchedule?.name}` : null} Payroll
								<div className="help-block">{status?.message || status?.title}</div>
							</h4>
						</div>
						<div className="pull-right">
							<div
								className="chip"
								style={{ background: "#ffdecd", color: "#ea611c", marginLeft: 0 }}
							>
								{periodDuration}
							</div>
							<div className="help-block text-right">of {year}</div>
						</div>
					</div>
				}
				content={
					<>
						<div>
							<BatteryChart {...{ currency, total, columns, title: status?.message }} />
						</div>
						<div style={{ paddingRight: "1rem", paddingTop: "1rem" }}>
							<div className="pull-right">
								<IntegrationSendPayrollData
									period={period}
									periodId={period.id}
								/>
							</div>
							<div className="pull-right">
								<Tooltip content="Payslips sent" position="top" style={{ display: period?.payslipsSent ? "" : "none", height: "22.5px" }}>
									<div style={{ backgroundColor: 'black', height: '22.5px', width: '22.5px', borderRadius: '50%', marginTop: "1px" }}>
										<BanknotesIcon style={{ color: "white", width: "16px", height: "16px", marginTop: "4px", marginLeft: "3px" }} />
									</div>
								</Tooltip>
								<IntegrationPayments period={period} />
							</div>
						</div>

						<div className="clearfix" />
					</>
				}
				footer={
					<div style={{ display: "flex", justifyContent: "space-between" }}>
						{userPermissions != "Read" && status ? (
							<GoToPayroll
								view={view}
								status={status}
								to={to}
							/>
						) : null}
						{actionButtons}
					</div>
				}
			/>
		);

	return (
		<Row
			actionColumns={[0]}
			columns={[
				<div className="btn-group">
					{userPermissions != "Read" && status ? (
						<GoToPayroll
							status={status}
							to={to}
						/>
					) : null}
					{isAdmin ? (
						<CopyId
							view={view}
							id={id}
						/>
					) : null}
				</div>,
				<>
					<div className="text-left">
						<div
							className="chip"
							style={{ background: "#ffdecd", color: "#ea611c", marginLeft: 0 }}
						>
							{periodDuration}
						</div>
						<div className="help-block">
							{periodOrdinal} {paySchedule?.name} Payroll of {year}
						</div>
					</div>
				</>,
				view === "Normal" ? (
					<BatteryChart {...{ currency, total, columns, title: status?.message }} />
				) : (
					<>{summary ? `${currency}${roundTo2(summary?.net)}` : "N/A"}</>
				),
				view === "Normal" ? null : <>{summary ? `${currency}${roundTo2(summary?.totalTaxDue)}` : "N/A"}</>,
				<>
					<div style={{ display: "flex", marginTop: 7, flexDirection: "row-reverse" }}>
						{actionButtons}
						<div style={{ display: "flex", marginRight: "1.5rem" }}>
							<Tooltip content="Payslips sent" position="top" style={{ display: period?.payslipsSent ? "" : "none", height: "22.5px" }}>
								<div style={{ backgroundColor: 'black', height: '22.5px', width: '22.5px', borderRadius: '50%', marginTop: "1px" }}>
									<BanknotesIcon style={{ color: "white", width: "16px", height: "16px", marginTop: "4px", marginLeft: "1px" }} />
								</div>
							</Tooltip>
							<IntegrationPayments period={period} />
							<IntegrationSendPayrollData
								period={period}
								periodId={period.id}
							/>
						</div >
					</div >
				</>,
				null,
			]}
		/>
	);
};

const mapStateToProps = (state, { period }) => {
	const userPermissions = state?.permissions?.my?.permissions?.payroll;
	const isUpdating = state.payPeriods?.updating?.[period.id];

	return {
		bank: state.company?.settings?.bankDetails?.[0]?.bic == "VALLMTMT" ? "bov" : "hsbc",
		paySchedule: state.company.settings?.paySchedules?.find((s) => s.id == period?.paySchedule),
		isAdmin: state?.my?.profile?.isAdmin,
		userPermissions,
		currency: state?.company?.settings?.currency,
		isUpdating,
		jurisdiction: state.company.settings.jurisdiction,
		archivedStatus: state?.company?.settings?.status,
	};
};

const mapDispatchToProps = (dispatch) => ({
	// sendPayPeriodToXero: (id) => dispatch(PAYROLL_ACTIONS.sendPayPeriodToXero(id)),

	sendPayslipsModal: (id) => dispatch(PAYSLIP_ACTIONS.sendPayslipsModal(true, id)),
	concludePayroll: (id) => dispatch(PAYROLL_ACTIONS.concludePayroll({ id })),
	downloadAllPayslips: (id) => dispatch(PAYROLL_ACTIONS.downloadAllPayslips(id)),
	downloadEmployeeForm: (data) => dispatch(FSS_ACTIONS.downloadEmployeeForm(data)),
	payrollUpdateId: (id) => dispatch(PAYROLL_ACTIONS.payrollUpdateId(id)),
	downloadReport: (reportType, payPeriods, year, period) =>
		dispatch(PAYROLL_ACTIONS.downloadReport(reportType, payPeriods, year, period)),
	setBankTransferPeriod: (period) => dispatch(COMPANY_ACTIONS.setBankTransferPeriod(period)),
	setBankTransferModal: () => dispatch(COMPANY_ACTIONS.setBankTransferModal(true)),
	getPayrollUpdate: (payPeriodId) => dispatch(FYORIN_ACTIONS.getPayrollUpdate(payPeriodId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PayrollRow);
